import { useState } from 'react';
import { useAuth } from '@/hooks/use-auth';
import { useProfileCompletion } from '@/hooks/use-profile-completion';
import { useClerk } from '@clerk/clerk-react';
import { useLocation } from 'wouter';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';
import { LogIn, LogOut, User, Settings, Wrench, UserPlus } from 'lucide-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';

const isDev = import.meta.env.DEV;

export function AuthButton() {
  const { user, isLoading, isAuthenticated, logout } = useAuth();
  const { isProfileComplete } = useProfileCompletion();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [location, setLocation] = useLocation();
  const [showProfile, setShowProfile] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [devLoading, setDevLoading] = useState(false);

  const updateProfile = useMutation({
    mutationFn: async (data: { firstName: string; lastName: string; phoneNumber: string }) => {
      const res = await fetch('/api/auth/user/profile', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(data),
      });
      if (!res.ok) throw new Error('Failed to update profile');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/auth/user'] });
      toast({ title: 'Profile updated', description: 'Your profile has been saved.' });
      setShowProfile(false);
    },
    onError: () => {
      toast({ title: 'Error', description: 'Failed to save profile. Please try again.', variant: 'destructive' });
    },
  });

  const openProfile = () => {
    setFirstName(user?.firstName || '');
    setLastName(user?.lastName || '');
    setPhone((user as any)?.phoneNumber || '');
    setEmail(user?.email || '');
    setShowProfile(true);
  };

  if (isLoading) {
    return (
      <div className="flex items-center gap-1.5 text-xs text-gray-400">
        <div className="w-4 h-4 rounded-full bg-gray-200 animate-pulse" />
      </div>
    );
  }

  const handleDevLogin = async () => {
    setDevLoading(true);
    try {
      const res = await fetch('/api/dev/admin-session', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });
      if (!res.ok) throw new Error('Dev login failed');
      queryClient.invalidateQueries({ queryKey: ['/api/auth/user'] });
      toast({ title: 'Logged in', description: 'Signed in as Dev Admin.' });
    } catch {
      toast({ title: 'Error', description: 'Dev login failed. Try again.', variant: 'destructive' });
    } finally {
      setDevLoading(false);
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="flex items-center gap-2">
        <Button
          size="sm"
          className="gap-1.5 text-xs font-semibold bg-[#FACC15] hover:bg-yellow-300 text-[#064E3B] border-0"
          onClick={() => setLocation(`/auth?mode=signup&returnTo=${encodeURIComponent(location)}`)}
          data-testid="button-signup"
        >
          <UserPlus className="w-3.5 h-3.5" />
          Create Account
        </Button>
        <Button
          size="sm"
          variant="outline"
          className="gap-1.5 text-xs font-semibold border-[#064E3B] text-[#064E3B] hover:bg-emerald-50"
          onClick={() => setLocation(`/auth?mode=signin&returnTo=${encodeURIComponent(location)}`)}
          data-testid="button-login"
        >
          <LogIn className="w-3.5 h-3.5" />
          Sign In
        </Button>
        {isDev && (
          <Button
            size="sm"
            variant="outline"
            className="gap-1.5 text-xs font-medium border-gray-300 text-gray-500 hover:text-[#064E3B]"
            data-testid="button-dev-login"
            onClick={handleDevLogin}
            disabled={devLoading}
          >
            <Wrench className="w-3 h-3" />
            {devLoading ? '...' : 'Dev'}
          </Button>
        )}
      </div>
    );
  }

  return (
    <>
      <div className="flex items-center gap-2">
        {!isProfileComplete && (
          <button
            onClick={openProfile}
            className="flex items-center gap-1 bg-amber-100 border border-amber-300 text-amber-800 text-xs font-semibold px-2 py-0.5 rounded-full hover:bg-amber-200 transition-colors"
            data-testid="badge-complete-profile"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-amber-500 flex-shrink-0" />
            Complete Profile
          </button>
        )}
        <button
          onClick={openProfile}
          className="flex items-center gap-1.5 cursor-pointer hover:opacity-80"
          data-testid="button-open-profile"
        >
          {user?.profileImageUrl ? (
            <img
              src={user.profileImageUrl}
              alt=""
              className="w-6 h-6 rounded-full border-2 border-[#064E3B]"
            />
          ) : (
            <div className="w-6 h-6 rounded-full bg-[#064E3B] flex items-center justify-center">
              <User className="w-3.5 h-3.5 text-[#FACC15]" />
            </div>
          )}
          <span className="text-xs font-medium text-gray-700 max-w-[80px] truncate">
            {user?.firstName || user?.email || 'User'}
          </span>
        </button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => logout()}
          className="gap-1 text-xs px-2 h-7 text-gray-500 hover:text-[#064E3B]"
          data-testid="button-logout"
        >
          <LogOut className="w-3 h-3" />
        </Button>
      </div>

      <Dialog open={showProfile} onOpenChange={setShowProfile}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings className="w-5 h-5 text-[#064E3B]" />
              Profile Settings
            </DialogTitle>
            <DialogDescription>
              Set up your profile so other players can find and identify you.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="firstName" className="text-sm font-medium">First Name</Label>
                <Input
                  id="firstName"
                  placeholder="First"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  data-testid="input-first-name"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="lastName" className="text-sm font-medium">Last Name</Label>
                <Input
                  id="lastName"
                  placeholder="Last"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  data-testid="input-last-name"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="phone" className="text-sm font-medium">Phone Number</Label>
              <Input
                id="phone"
                type="tel"
                placeholder="(555) 123-4567"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                data-testid="input-phone"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email" className="text-sm font-medium">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="input-email"
              />
            </div>
          </div>
          <DialogFooter className="flex gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => setShowProfile(false)}
              data-testid="button-cancel-profile"
            >
              Cancel
            </Button>
            <Button
              onClick={() => updateProfile.mutate({ firstName, lastName, phoneNumber: phone })}
              disabled={updateProfile.isPending}
              className="bg-[#064E3B] hover:bg-[#053f30] text-[#FACC15]"
              data-testid="button-save-profile"
            >
              {updateProfile.isPending ? 'Saving...' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
